import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {
  CampaignAnalysisRunResultsRow,
  SalesLiftResponseVariable,
  SingleResult,
  WeeklyCampaignAnalysisRunResultsRow,
} from 'utils/types';

export type CampaignAnalysisRunResultsParams = {
  campaignAnalysisRunId: number | string;
  itemGroup: 'combined' | 'hero' | 'halo';
  responseVariable: SalesLiftResponseVariable;
  zeroNegativeSales: boolean;
  removeOutliers: boolean;
  includeTopNumItems?: number;
  includeTopNumRegions?: number;
};

const buildQuery = (params: CampaignAnalysisRunResultsParams): string => {
  const query = new URLSearchParams({
    campaign_analysis_run_id: String(params.campaignAnalysisRunId),
    item_group: params.itemGroup,
    response_variable: params.responseVariable,
    zero_negative_sales: String(params.zeroNegativeSales),
    remove_outliers: String(params.removeOutliers),
  });
  if (params.includeTopNumItems != null) {
    query.set('include_top_num_items', String(params.includeTopNumItems));
  }
  if (params.includeTopNumRegions != null) {
    query.set('include_top_num_regions', String(params.includeTopNumRegions));
  }
  return query.toString();
};

export interface AnalysisRunResultsState {
  /** Single results row for the active discriminator combo. */
  results: SingleResult<CampaignAnalysisRunResultsRow | null>;
  /** Per-week results rows for the active discriminator combo, ordered by week_ending. */
  weeklyResults: SingleResult<WeeklyCampaignAnalysisRunResultsRow[]>;
}

const initialState: AnalysisRunResultsState = {
  results: {as: 'idle'},
  weeklyResults: {as: 'idle'},
};

export const analysisRunResultsEndpoints = {
  getCampaignAnalysisRunResults: createAsyncThunk(
    'analysisRunResults/getCampaignAnalysisRunResults',
    async (params: CampaignAnalysisRunResultsParams) => {
      const response = await ApiService.get(`/campaign_analysis_run_results/?${buildQuery(params)}`);
      const rows = (response.data ?? []) as CampaignAnalysisRunResultsRow[];
      return {value: rows[0] ?? null};
    },
  ),
  getWeeklyCampaignAnalysisRunResults: createAsyncThunk(
    'analysisRunResults/getWeeklyCampaignAnalysisRunResults',
    async (params: CampaignAnalysisRunResultsParams) => {
      const response = await ApiService.get(`/campaign_analysis_run_weekly_results/?${buildQuery(params)}`);
      const rows = (response.data ?? []) as WeeklyCampaignAnalysisRunResultsRow[];
      return {value: rows};
    },
  ),
};

export const analysisRunResultsSlice = createSlice({
  name: 'analysisRunResults',
  initialState,
  reducers: {
    clearAnalysisRunResults(state) {
      state.results = {as: 'idle'};
      state.weeklyResults = {as: 'idle'};
    },
  },
  extraReducers: builder => {
    addResultCases(builder, analysisRunResultsEndpoints.getCampaignAnalysisRunResults, 'results');
    addResultCases(builder, analysisRunResultsEndpoints.getWeeklyCampaignAnalysisRunResults, 'weeklyResults');
  },
});

export const analysisRunResultsActions = analysisRunResultsSlice.actions;

export type AnalysisRunResultsSlice = {
  [analysisRunResultsSlice.name]: ReturnType<(typeof analysisRunResultsSlice)['reducer']>;
};
